/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { FloatingTooltip } from '@coursera-web/cds-labs';
import type { FloatingTooltipProps } from '@coursera-web/cds-labs';
import { ReactEditor, useSlateStatic } from 'slate-react';

import type { ButtonProps } from '@coursera/cds-core';
import { Button as CdsButton } from '@coursera/cds-core';

import {
  type ButtonRole,
  TOOLBAR_BUTTON_TYPES,
  type ToolbarButtonType,
} from 'bundles/cml/editor/components/buttons/types';
import {
  getAriaAttributes,
  isLeftKey,
  isRightKey,
  shouldFocusOnClick,
} from 'bundles/cml/editor/components/buttons/utils';
import { useManagedFocus } from 'bundles/cml/editor/components/utils/managedFocusContext';
import { useFocusedContext } from 'bundles/cml/editor/context/focusContext';
import { mergeRefs } from 'bundles/cml/editor/utils/mergeRefs';

const styles = {
  button: css`
    flex: none;
    white-space: nowrap;
    color: var(--cds-color-neutral-primary);

    svg {
      color: var(--cds-color-neutral-primary);
      fill: var(--cds-color-neutral-primary);
    }

    &:hover {
      text-decoration: none;
      color: var(--cds-color-emphasis-primary-content-default);
      background: var(--cds-color-neutral-background-primary-weak);

      svg {
        color: var(--cds-color-emphasis-primary-content-default);
        fill: var(--cds-color-emphasis-primary-content-default);
      }
    }

    &:disabled {
      svg {
        color: var(--cds-color-grey-400);
        fill: var(--cds-color-grey-400);
      }
    }

    span:first-of-type {
      min-height: 16px;
    }

    .cds-button-label {
      position: relative;
    }
  `,
  active: css`
    color: var(--cds-color-emphasis-primary-content-default);
    background: var(--cds-color-interactive-background-primary-hover-weak);

    svg {
      color: var(--cds-color-emphasis-primary-content-default);
      fill: var(--cds-color-emphasis-primary-content-default);
    }

    .cds-button-label {
      ::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        height: 2px;
        width: 100%;
        background: var(--cds-color-emphasis-primary-content-default);
      }
    }
  `,
  inactive: css`
    color: var(--cds-color-grey-400);

    svg {
      color: var(--cds-color-grey-400);
      fill: var(--cds-color-grey-400);
    }

    pointer-events: none;
  `,
  inactiveSecondary: css`
    background: var(--cds-color-neutral-background-primary-weak);
    box-shadow: inset 0 0 0 1px var(--cds-color-neutral-disabled-strong);
  `,
  defaultButtonStyles: css`
    &:disabled {
      svg {
        color: var(--cds-color-grey-400);
      }
    }
  `,
};

export { TOOLBAR_BUTTON_TYPES };

export type Props = React.PropsWithChildren<{
  active?: boolean;
  disabled?: boolean;
  onClick?: React.ReactEventHandler<HTMLButtonElement>;
  className?: string;
  title?: string;
  buttonProps?: Partial<ButtonProps>;
  useDefaultCdsColor?: boolean;
  role?: ButtonRole;
  tooltipProps?: Partial<FloatingTooltipProps>;
  type: ToolbarButtonType;
}>;

const Button = React.forwardRef<HTMLButtonElement, Props>((props, parentRef) => {
  const { focused } = useFocusedContext();
  const { focusNext, focusPrevious, focusedButton, setFocusedButton } = useManagedFocus();
  const ref = useRef<HTMLButtonElement>(null);
  const staticEditor = useSlateStatic();

  const {
    active,
    onClick,
    children,
    useDefaultCdsColor = false,
    title,
    tooltipProps,
    buttonProps,
    role = 'button',
    disabled,
    type,
    ...attributes
  } = props;

  // The "pageless" toolbar must capture and preventDefault onmousedown so the editor doesn't
  // lose focus. While the standard toolbars must capture onclick in order to prevent global
  // mousedown event listeners (like Quiz Questions, etc...) from preempting our handler
  // (see CP-11575)
  const handleMouseDown = (event: React.MouseEvent) => event.preventDefault();
  const handleClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
      event.preventDefault();

      if (shouldFocusOnClick(type)) {
        ReactEditor.focus(staticEditor);
      }

      onClick?.(event as React.MouseEvent<HTMLButtonElement>);
    },
    [onClick, staticEditor, type]
  );

  const handleKeyDown = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.KeyboardEvent) => {
      const { nativeEvent } = event;
      if (isLeftKey(nativeEvent)) {
        focusPrevious();
      } else if (isRightKey(nativeEvent)) {
        focusNext();
      }
    },
    [focusNext, focusPrevious]
  );

  const handleFocus = useCallback<() => $TSFixMe>(() => {
    setFocusedButton(ref.current);
  }, [setFocusedButton]);

  const mergedRefs = useMemo(() => mergeRefs([parentRef, ref]), [parentRef, ref]);

  const ariaAttributes = getAriaAttributes({ title, role, type, active });

  return (
    <FloatingTooltip show={!disabled && !!title} message={title} offset={3} {...tooltipProps}>
      <CdsButton
        tabIndex={focusedButton === ref.current ? 0 : -1}
        role={role}
        ref={mergedRefs}
        variant="ghost"
        size="small"
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onMouseDownCapture={handleMouseDown}
        onClickCapture={handleClick}
        {...ariaAttributes}
        disabled={disabled}
        {...buttonProps}
        {...attributes}
        css={[
          useDefaultCdsColor ? styles.defaultButtonStyles : styles.button,
          !focused && styles.inactive,
          !focused && useDefaultCdsColor && styles.inactiveSecondary,
          active && styles.active,
        ]}
      >
        {children}
      </CdsButton>
    </FloatingTooltip>
  );
});

export default Button;
